import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Button from '../../components/Buttons/Button';
import CollapsibleCard from '../../components/Card/CollapsibleCard';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import { BusinessPartnerTypeEnum } from '../../enums/BusinessPartnerTypeEnum';
import WorkflowTaskModel from '../../models/WorkflowTaskModel';
import WorkflowTaskQueryModel from '../../models/WorkflowTaskQueryModel';
import DataService from "../../services/data.service";
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import WorkflowTaskPopup from './Popups/WorkflowTask_popup_WorkflowTask';

interface IWorkflowTaskSearchCriteria {
    quickSearch?: string
    workflowDefinitionId?: number
    workflowStatusId?: number
    projectIds: number[]
    assignedToUserId?: number
    customerIds: number[]
    supplierIds: number[]
    licensorId?: number
    brandId?: number
    departmentId?: number
    divisionId?: number
    mainWorkflowsOnly: boolean
    suspendedWorkflowsOnly: boolean
    activeWorkflowsOnly: boolean
    rememberFilter: boolean
    showSummary: boolean

    pageNumber: number
    pageSize: number
    sortBy: string
}

interface WorkflowSearchResult {
    workflow: string
    task: string
    category: string
    sevenDays: WorkflowTaskQueryModel[]
    twoWeeks: WorkflowTaskQueryModel[]
    oneMonth: WorkflowTaskQueryModel[]
    twoMonths: WorkflowTaskQueryModel[]
    threeMonths: WorkflowTaskQueryModel[]
    sixMonths: WorkflowTaskQueryModel[]
    oneYear: WorkflowTaskQueryModel[]
    moreThenOneYear: WorkflowTaskQueryModel[]
}

const SearchWorkflowTaskView: React.FC = () => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<IWorkflowTaskSearchCriteria>()
    const [tableData, setTableData] = useState<WorkflowTaskQueryModel[]>();
    const [searchResult, setSearchResult] = useState<WorkflowSearchResult[]>([])
    const [isSummaryCollapsed, setSummaryCollapsed] = useState(true)
    const [workflowTaskId, setWorkflowTaskId] = useState(0)
    const [showTaskPopup, setShowTaskPopup] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const formatUtils = new FormatUtils()

    const workflowDefinitions = useSelectListOptions(`api/v1/selectlist/WorkflowDefinitions?activeOnly=${formData?.activeWorkflowsOnly ?? true}&applicationFunctionId=${ApplicationFunctions.SearchWorkflowTask}`, '-- All workflows');
    const workflowStatuses = useSelectListOptions('api/v1/selectlist/WorkflowStatuses', '-- All statuses');
    const taskCategories = useSelectListOptions('api/v1/selectlist/WorkflowTaskCategories', '-- All categories');
    const projects = useSelectListOptions('api/v1/selectlist/Projects?activeOnly=' + formData?.activeWorkflowsOnly ?? true);
    const users = useSelectListOptions('api/v1/selectlist/Users?activeOnly=' + formData?.activeWorkflowsOnly ?? true, '-- All users');
    const customers = useSelectListOptions('api/v1/selectlist/BusinessPartners?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Customer,);
    const suppliers = useSelectListOptions('api/v1/selectlist/BusinessPartners?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Supplier,);
    const licensors = useSelectListOptions('api/v1/selectlist/BusinessPartners?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Licensor, '-- All licensors');
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees?activeOnly=' + formData?.activeWorkflowsOnly ?? true, '-- All brands');
    const departments = useSelectListOptions('api/v1/selectlist/Departments', '-- All departments');
    const divisions = useSelectListOptions('api/v1/selectlist/Divisions', '-- All divisions');

    useEffect(() => {
        if (formData && user) {
            setFormData({ ...formData, assignedToUserId: user.userId });
        }

    }, [user])

    function setDefaultSearchCriteria() {
        var defaultSearchCriteria: IWorkflowTaskSearchCriteria = {
            projectIds: [],
            customerIds: [],
            supplierIds: [],
            mainWorkflowsOnly: false,
            suspendedWorkflowsOnly: false,
            activeWorkflowsOnly: true,
            rememberFilter: formData?.rememberFilter ?? false,
            showSummary: true,
            pageNumber: 1,
            pageSize: 10000,
            sortBy: 'CreatedOn'
        }

        setFormData({ ...defaultSearchCriteria });
    }

    function submit() {
        getTableData(formData);
    }

    useEffect(() => {
        const filterItem = localStorage.getItem("workflowTasksFilter");
        if (filterItem !== null) {
            let filter: IWorkflowTaskSearchCriteria = JSON.parse(filterItem);
            setFormData(filter)
            getTableData(filter)
        }
        else {
            setDefaultSearchCriteria()
        }
    }, [])

    function getTableData(searchCriteria) {
        setSummaryCollapsed(true)
        setShowSpinner(true);

        if (searchCriteria.rememberFilter)
            localStorage.setItem('workflowTasksFilter', JSON.stringify(searchCriteria));
        else
            localStorage.removeItem(`workflowTasksFilter`);

        setTableData(undefined)
        setSearchResult([])
        DataService
            .post<DataResult<WorkflowTaskQueryModel[]>>(`api/v1/WorkflowTasks/Search/`, searchCriteria)
            .then(response => {
                setShowSpinner(false);
                handleResult(response);
                if (response.isSuccesful === true && response.data) {
                    CreateSearchResult(response.data)
                }
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function CreateSearchResult(result: WorkflowTaskQueryModel[])
    {
        const formatUtils = new FormatUtils();
        const today = new Date(Date.now())

        const searchResults: WorkflowSearchResult[] = []
        result.forEach((task) => {
            let searchResultItem: WorkflowSearchResult

            let items = searchResults.filter(x => x.task == task.task)

            if (items.length > 0)
                searchResultItem = items[0]
            else {
                searchResultItem = {
                    workflow: task.workflow,
                    task: task.task,
                    category: task.taskCategory,
                    sevenDays: [],
                    twoWeeks: [],
                    oneMonth: [],
                    twoMonths: [],
                    threeMonths: [],
                    sixMonths: [],
                    oneYear: [],
                    moreThenOneYear: []
                }
                searchResults.push(searchResultItem)
            }
            let taskDate: Date|undefined = formatUtils.dateFromString(task.createdOn);
            if (taskDate) {
                let dateDiff = (today.getTime() - taskDate.getTime()) / (1000 * 3600 * 24)
                if (dateDiff < 7)
                    searchResultItem.sevenDays.push(task);
                else if (dateDiff < 14)
                    searchResultItem.twoWeeks.push(task);
                else if (dateDiff < 30)
                    searchResultItem.oneMonth.push(task);
                else if (dateDiff < 60)
                    searchResultItem.twoMonths.push(task);
                else if (dateDiff < 90)
                    searchResultItem.threeMonths.push(task);
                else if (dateDiff < 180)
                    searchResultItem.sixMonths.push(task);
                else if (dateDiff < 365)
                    searchResultItem.oneYear.push(task);
                else
                    searchResultItem.moreThenOneYear.push(task);

            }
        })
        setSearchResult(searchResults)
        setSummaryCollapsed(false)

        if (formData?.showSummary === false)
            setTableData(result)
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 3 },
            { name: 'workflowDefinitionId', label: 'Workflow', labelSize: 3 },
            { name: 'workflowStatusId', label: 'Status', labelSize: 3 },
            { name: 'taskCategoryId', label: 'Category', labelSize: 3 },
            { name: 'projectIds', label: 'Projects', labelSize: 3 },
            { name: 'assignedToUserId', label: 'Assigned to', labelSize: 3 },
            { name: 'connectedToUserId', label: 'Connected to', labelSize: 3 },
            { name: 'customerIds', label: 'Customers', labelSize: 3 },
            { name: 'supplierIds', label: 'Suppliers', labelSize: 3 },
            { name: 'licensorId', label: 'Licensor', labelSize: 3 },
            { name: 'brandId', label: 'Brands', labelSize: 3 },
            { name: 'departmentId', label: 'Department', labelSize: 3 },
            { name: 'divisionId', label: 'Division', labelSize: 3 },
            { name: 'suspendedWorkflowsOnly', label: 'Suspended tasks only', labelSize: 3 },
            { name: 'activeWorkflowsOnly', label: 'Active tasks only', labelSize: 3 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 3 },
            { name: 'showSummary', label: 'Show summary', labelSize: 3 },

            { name: 'pageSize', label: 'Nr of Tasks', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.SearchWorkflowTask))
        return (<Unauthorized />);

    function getStatusColor(rowData: WorkflowTaskModel, colInfo: IColInfo): string | undefined {
        const now = new Date(Date.now())
        const dueOn = formatUtils.dateFromString(rowData.dueOn);
        const completedOn = rowData.completedOn
            ? formatUtils.dateFromString(rowData.completedOn)
            : undefined;

        if (dueOn === undefined || dueOn >= now || (completedOn && completedOn <= dueOn))
            return 'Green';

        return 'red';
    }

    function onCellClick(rowData: WorkflowSearchResult, colInfo: IColInfo)
    {
        switch (colInfo.dataProp)
        {
            case 'sevenDays.length': setTableData(rowData.sevenDays); break;
            case 'twoWeeks.length': setTableData(rowData.twoWeeks); break;
            case 'oneMonth.length': setTableData(rowData.oneMonth); break;
            case 'twoMonths.length': setTableData(rowData.twoMonths); break;
            case 'threeMonths.length': setTableData(rowData.threeMonths); break;
            case 'sixMonths.length': setTableData(rowData.sixMonths); break;
            case 'oneYear.length': setTableData(rowData.oneYear); break;
            case 'moreThenOneYear.length': setTableData(rowData.moreThenOneYear); break;

            default:
                var data: WorkflowTaskQueryModel[] = []
                data = data.concat(rowData.sevenDays)
                data = data.concat(rowData.twoWeeks)
                data = data.concat(rowData.oneMonth)
                data = data.concat(rowData.twoMonths)
                data = data.concat(rowData.threeMonths)
                data = data.concat(rowData.sixMonths)
                data = data.concat(rowData.oneYear)
                data = data.concat(rowData.moreThenOneYear)

                setTableData(data)
                break
        }
        setSummaryCollapsed(true)
    }

    function onSelectTaskRow(rowData: WorkflowTaskQueryModel) {
        setWorkflowTaskId(rowData.id)
        setShowTaskPopup(true)
    }

    function onTaskPopupClosed(submittedTaskIds: number[]) {
        setShowTaskPopup(false)
        if (tableData) {
            submittedTaskIds.forEach(taskId => { 
                var task = tableData.filter(x => x.id == taskId)[0]
                var index = tableData.indexOf(task)
                if (index >= 0)
                    tableData.splice(index, 1)
            })

            setTableData([...tableData])
        }
    }

    return (
        <div className='main-content'>
            <Form title='Workflow tasks' footer={(
                <Row>
                    <Col md='6'>
                        <ButtonBar offset={3}>
                            <Button text='Search' {...getSubmitButtonProps()} />
                            <Button text='Clear' type='button' onClick={setDefaultSearchCriteria} />
                        </ButtonBar>
                    </Col>
                    <Col md='6' className='hidden-md'>
                        <ButtonBar offset={0} />
                    </Col>
                </Row>)}>
                <Container fluid>
                    <Row>
                        <Col md='4'>
                            <TextboxField {...getFieldProps('quickSearch')} />
                            <SelectListField {...getFieldProps('workflowDefinitionId')} options={workflowDefinitions} />
                            <SelectListField {...getFieldProps('workflowStatusId')} options={workflowStatuses} />
                            <SelectListField {...getFieldProps('taskCategoryId')} options={taskCategories} />
                            <SelectListField {...getFieldProps('brandId')} options={brands} />
                            <MultiSelectField {...getFieldProps('customerIds')} options={customers} />
                            <MultiSelectField {...getFieldProps('supplierIds')} options={suppliers} />
                        </Col>
                        <Col md='4'>
                            <MultiSelectField {...getFieldProps('projectIds')} options={projects} />
                            <SelectListField {...getFieldProps('assignedToUserId')} options={users} />
                            <SelectListField {...getFieldProps('connectedToUserId')} options={users} />
                            <SelectListField {...getFieldProps('departmentId')} options={departments} />
                            <SelectListField {...getFieldProps('divisionId')} options={divisions} />
                        </Col>
                        <Col md='4'>
                            <SwitchField {...getFieldProps('suspendedWorkflowsOnly')} />
                            <SwitchField {...getFieldProps('activeWorkflowsOnly')} />
                            <SwitchField {...getFieldProps('rememberFilter')} />
                            <SwitchField {...getFieldProps('showSummary')} />
                        </Col>

                    </Row>
                </Container>
            </Form>
            {formData?.showSummary && 
                <CollapsibleCard isCollapsed={isSummaryCollapsed} toggleCollapse={setSummaryCollapsed} style={{ margin: 12, marginTop: 0 }}>
                    <div style={{ margin: 6 }}>
                        <DataTable
                            name='dtSearchWorkflowTaskSummary'
                            colInfos={[
                                { title: 'Workflow', dataProp: 'workflow', onCellClick: onCellClick, filterInfo: { filterType: 'CheckboxList', filterMenuHeight: 400, filterMenuWidth: 400 } },
                                { title: 'Task', dataProp: 'task', onCellClick: onCellClick, filterInfo: { filterType: 'CheckboxList', filterMenuHeight: 400, filterMenuWidth: 400 } },
                                { title: 'Category', dataProp: 'taskCategory', onCellClick: onCellClick, filterInfo: { filterType: 'CheckboxList' } },
                                { title: '< 7 days', dataProp: 'sevenDays.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 2 weeks', dataProp: 'twoWeeks.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 1 month ', dataProp: 'oneMonth.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 2 months', dataProp: 'twoMonths.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 3 months', dataProp: 'threeMonths.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 6 months', dataProp: 'sixMonths.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '<= 1 year', dataProp: 'oneYear.length', dataType: 'number', onCellClick: onCellClick },
                                { title: '> 1 year', dataProp: 'moreThenOneYear.length', dataType: 'number', onCellClick: onCellClick },
                            ]}
                            tableData={searchResult}
                        />
                    </div>
                </CollapsibleCard>
            }
            {tableData && (
                <div className="card" style={{ margin: 12, padding: 12 }}>
                    <DataTable
                        name='dtSearchWorkflowTask'
                        colInfos={[
                            { key: true, dataProp: 'id', visible: false },
                            { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
                            { title: 'Workflow', dataProp: 'workflow', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Sub-Items', dataProp: 'itemRevisionColors', dataType: 'workflowItemRevisions', sort: false },
                            { title: 'Task', dataProp: 'task', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Category', dataProp: 'taskCategory', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Project', dataProp: 'project', dataType: 'project', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Item Nr', dataProp: 'itemRevision.item', dataType: 'item', filterInfo: { filterType: 'Textbox' } },
                            { title: 'Item Name', dataProp: 'itemRevision.item.name', filterInfo: { filterType: 'Textbox' } },
                            { title: 'Revision', dataProp: 'itemRevision.revisionNumber', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Assigned to', dataProp: 'assignedToUser', dataType: 'user', filterInfo: { filterType: 'CheckboxList' } },
                            { title: 'Assigned on', dataProp: 'assignedOn', dataType: 'datetime', sort: 'desc' },
                            { title: 'Due on', dataProp: 'dueOn', dataType: 'datetime' },
                            { title: 'Completed on', dataProp: 'completedOn', dataType: 'datetime' },
                            { title: 'Chosen Action', dataProp: 'chosenWorkflowAction' },
                            { title: 'Next Action', dataProp: 'nextAction' },
                            { title: 'Status', dataProp: 'workflowStatus', filterInfo: { filterType: 'CheckboxList' } },
                        ]}
                        tableData={tableData}
                        pageSize={20}
                        onRowClick={onSelectTaskRow} 
                    />
                </div>
            )}
            {showSpinner && (<SpinnerOverlay />)}
            {showTaskPopup && <WorkflowTaskPopup show={showTaskPopup} onHide={onTaskPopupClosed} workflowTaskId={workflowTaskId} />}
        </div>
    );
}
export default SearchWorkflowTaskView;
